import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Star, ArrowRight } from 'lucide-react';
import { BortleBadge } from './BortleBadge';
import type { NearbyLocation } from '@/lib/locations';

interface Props {
  /** Up to six nearest locations, computed by the page's loader at build time (ERI-2594). */
  locations: NearbyLocation[];
  locationName: string;
}

export const NearbyLocations: React.FC<Props> = ({ locations }) => {
  if (locations.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground text-sm uppercase tracking-wide">
          Nearby Dark Sky Locations
        </h3>
        <Link
          to="/dark-sky-locations"
          className="text-xs text-[#c9922a] hover:underline flex items-center gap-1"
        >
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {locations.map((loc) => (
          <Link key={loc.id} to={`/dark-sky-locations/${loc.slug}`} className="group">
            <Card className="overflow-hidden rounded-[4px] hover:shadow-md transition-shadow">
              <div className="flex">
                {/* Thumbnail */}
                <div className="w-20 h-20 flex-shrink-0 bg-[#0f1923]">
                  {loc.featured_image_url ? (
                    <img src={loc.featured_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Star className="w-5 h-5 text-white/20" />
                    </div>
                  )}
                </div>

                <CardContent className="p-2.5 flex-1 min-w-0">
                  <h4 className="font-medium text-foreground text-sm leading-tight truncate group-hover:text-[#c9922a] transition-colors">
                    {loc.name}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {Math.round(loc.distance_miles)}mi
                    </span>
                    {loc.bortle_class && (
                      <BortleBadge bortle={loc.bortle_class} size="sm" />
                    )}
                  </div>
                </CardContent>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};
